export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      accept: 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: any = null;
  if (text) { try { body = JSON.parse(text); } catch { body = text; } }
  if (!res.ok) throw new ApiError(res.status, body?.error ?? res.statusText);
  return body as T;
}

export const api = {
  get: <T,>(p: string) => request<T>(p),
  post: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T,>(p: string, body: unknown) =>
    request<T>(p, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T,>(p: string) => request<T>(p, { method: 'DELETE' }),
};

// ---------------------------------------------------------------- shapes

export type CheckStatus = 'pass' | 'fail' | 'warn' | 'skip';
export interface Check { id: string; label: string; status: CheckStatus; detail?: string }

export interface AdminUser {
  id: string; email: string; role: 'super_admin' | 'tenant_admin';
  disabled: boolean; createdAt: string; tenantIds: string[];
}

export interface TenantSummary {
  id: string; slug: string; name: string; description: string; createdAt: string;
  counts: { saml: number; oidc: number; ping: number };
}

export interface Tenant {
  id: string; slug: string; name: string; description: string;
  spCertificate: string; createdAt: string; updatedAt: string;
  selfService: boolean; lastActiveAt: string;
  publicBaseUrl: string; portalUrl: string;
}

export interface SamlEndpoints {
  entityId: string; loginUrl: string; acsUrl: string; sloUrl: string; metadataUrl: string;
  nameIdFormat: string; signingCertificate: string; signingCertificateBase64: string;
  signingCertificateFingerprint: string; publicBaseUrl: string;
}

export interface SamlConnection {
  id: string; tenantId: string; name: string; enabled: boolean;
  idpEntityId: string; idpSsoUrl: string; idpSloUrl: string; idpCertificates: string[];
  spEntityId: string; nameIdFormat: string; authnContext: string[];
  forceAuthn: boolean; isPassive: boolean; signAuthnRequest: boolean;
  wantAssertionsSigned: boolean; wantResponseSigned: boolean;
  signatureAlgorithm: string; digestAlgorithm: string; clockSkewMs: number;
  audience: string; allowUnencryptedAssertion: boolean;
  endpoints: SamlEndpoints;
}

export interface OidcConnection {
  id: string; tenantId: string; name: string; enabled: boolean;
  issuer: string; useDiscovery: boolean; discoveryUrl: string;
  authorizationEndpoint: string; tokenEndpoint: string; userinfoEndpoint: string;
  jwksUri: string; introspectionEndpoint: string; revocationEndpoint: string;
  endSessionEndpoint: string; clientId: string; clientSecret: string;
  tokenAuthMethod: string; scopes: string; responseType: string; responseMode: string;
  pkceMethod: string; extraAuthParams: Record<string, string>;
  expectedAudience: string; verifyIdToken: boolean; fetchUserinfo: boolean; clockSkewS: number;
  endpoints: { loginUrl: string; redirectUri: string; postLogoutRedirectUri: string };
}

export interface PingConnection {
  id: string; tenantId: string; name: string; enabled: boolean;
  tokenHeader: string; tokenHeaderScheme: string; validateToken: boolean;
  jwksUri: string; expectedIssuer: string; expectedAudience: string; clockSkewS: number;
  identityHeaderPrefixes: string[]; requiredClaims: string[]; requiredHeaders: string[];
  endpoints: { appUrl: string; echoUrl: string; healthUrl: string };
}

export interface TestResult {
  id: string; tenantId: string; protocol: 'saml' | 'oidc' | 'ping';
  connectionId: string; connectionName: string;
  status: 'success' | 'warning' | 'error'; subject: string;
  summary: { checks: Check[]; trusted?: boolean; attributes?: Record<string, unknown>; [k: string]: any };
  detail: Record<string, any>;
  createdAt: string; expiresAt: string;
}

export interface ResultRow {
  id: string; protocol: 'saml' | 'oidc' | 'ping'; connectionName: string;
  status: 'success' | 'warning' | 'error'; subject: string; createdAt: string;
  checkCounts: { pass: number; fail: number; warn: number; skip: number };
}
